import React, { useCallback } from "react";
import { AgGridReact, AgGrid } from "@jsq/ds/core";
import { ClientSearch } from "#components/Search/ClientSearch";
import { ClientFilterParams } from "./types";

/**
 * AgGrid Client Filter Component
 *
 * A wrapper around ClientSearch that integrates with AgGrid's filtering system.
 * This component handles AgGrid-specific logic like filter callbacks and model changes.
 * Supports both single and multiselect modes.
 *
 * @param filterParams - AgGrid CustomFilterProps with optional ClientFilterParams
 * @returns A client filter component integrated with AgGrid
 *
 * @example
 * // Single select mode (default)
 * {
 *   field: 'clientName',
 *   filter: ClientFilter,
 *   filterParams: { ... }
 * }
 *
 * @example
 * // Multiselect mode
 * {
 *   field: 'clientName',
 *   filter: ClientFilter,
 *   filterParams: { multiselect: true }
 * }
 */
export const ClientFilter = (
  filterParams: AgGridReact.CustomFilterProps & ClientFilterParams,
) => {
  const { onModelChange, getValue, multiselect = false } = filterParams;

  const handleSelect = useCallback(
    (clientId?: string) => {
      onModelChange(clientId);
    },
    [onModelChange],
  );

  const handleMultiSelect = useCallback(
    (clientIds: string[]) => {
      onModelChange(clientIds.length > 0 ? clientIds : null);
    },
    [onModelChange],
  );

  const handleSearchClear = useCallback(() => {
    onModelChange(null);
  }, [onModelChange]);

  // Note: doesFilterPass is only called with the Client-Side Row Model.
  const doesFilterPass = useCallback(
    (params: AgGrid.IDoesFilterPassParams) => {
      const nodeValue = getValue(params.node) as string | undefined;
      const filterValue = filterParams.model as string | string[] | null;

      // If no filter is set, pass all rows
      if (!filterValue) {
        return true;
      }

      // Handle multiselect mode (array of values)
      if (Array.isArray(filterValue)) {
        return nodeValue ? filterValue.includes(nodeValue) : false;
      }

      // Handle single select mode
      return nodeValue == filterValue;
    },
    [getValue, filterParams],
  );

  // register filter callbacks with the grid
  AgGridReact.useGridFilter({ doesFilterPass });

  // Get current values for multiselect mode
  const currentValues =
    multiselect && Array.isArray(filterParams.model) ? filterParams.model : [];

  return (
    <ClientSearch
      multiselect={multiselect}
      values={currentValues}
      onSelect={handleSelect}
      onMultiSelect={handleMultiSelect}
      onSearchClear={handleSearchClear}
    />
  );
};
